// @flow

import * as React from 'react'
import Modal from 'react-modal'
import styled from '@emotion/styled'

import { Icon } from '@toggl/ui'
import { media, colors } from '@toggl/style'
import { transparentize } from 'polished'

type ModalVideoProps = {
  children?: React.Node,
  Content?: React.ComponentType<any>,
  placeholder?: string | React.Node,
  skewed?: boolean,
}

export default function ModalVideo({
  children,
  Content,
  placeholder,
  skewed,
  ...props
}: ModalVideoProps) {
  const [isModalOpen, setModalOpen] = React.useState(false)

  function openModal() {
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
  }

  return (
    <Root {...props}>
      <div>
        {skewed ? (
          <SkewedImgWrapper onClick={children && openModal}>
            {typeof placeholder === 'string' ? (
              <SkewedImg src={placeholder} />
            ) : (
              placeholder
            )}
            {children && (
              <Icon.Play color={colors.darkPurple} arrowColor={colors.beige} />
            )}
          </SkewedImgWrapper>
        ) : (
          <ImgWrapper>
            {typeof placeholder === 'string' ? (
              <Img src={placeholder} />
            ) : (
              placeholder
            )}
            {children && (
              <Icon.Play color={colors.darkPurple} arrowColor={colors.beige} />
            )}
          </ImgWrapper>
        )}

        {Content !== undefined && <Content />}
      </div>

      {children && (
        <Modal
          style={customStyles}
          isOpen={isModalOpen}
          onRequestClose={closeModal}
        >
          {children}

          <CloseOption
            onClick={() => {
              closeModal()
            }}
          >
            Close
          </CloseOption>
        </Modal>
      )}
    </Root>
  )
}

const Root: React.ComponentType<any> = styled.div`
  video,
  img {
    width: 100%;
  }

  svg {
    cursor: pointer;
  }
`

export const Img: React.ComponentType<any> = styled.img`
  width: 260px;

  ${media.mq[1]} {
    width: 388px;
  }
`

const ImgWrapper = styled.div`
  position: relative;

  svg {
    fill: ${colors.beige};
    position: absolute;
    width: 48px;
    height: 48px;
    top: calc(50% - 24px);
    left: calc(50% - 24px);
  }

  ${media.mq[1]} {
    svg {
      width: 62px;
      height: 62px;
      top: calc(50% - 31px);
      left: calc(50% - 31px);
    }
  }
`

const CloseOption = styled.span`
  position: fixed;
  top: 8px;
  right: 8px;
  padding: 10px;
  cursor: pointer;
  color: ${colors.beige};
  &:hover {
    color: ${(p) => p.theme.primary};
  }
`

const customStyles = {
  /* stylelint-disable selector-type-no-unknown */
  overlay: {
    background: transparentize(0.3, colors.fadedPurple),
    overflow: 'hidden',
    height: '100%',
    zIndex: '10000',
  },
  content: {
    position: 'absolute',
    top: '50px',
    left: '80px',
    right: '80px',
    bottom: '50px',
    padding: '0',
    border: 'none',
    background: 'transparent',
    overflow: 'hidden',
  },
  /* stylelint-enable */
}

export const SkewedImg: React.ComponentType<any> = styled.img``

const SkewedImgWrapper = styled.div`
  position: relative;
  text-align: center;
  margin-top: 50px;
  svg {
    width: 64px;
    height: 64px;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    z-index: 110;
    background: ${colors.darkPurple};
    border: 3px solid ${colors.beige};
    border-radius: 50%;
    cursor: pointer;
    transition: 0.35s ease-in-out;
    opacity: 0.9;
    &:hover {
      transform: scale(1.15);
      opacity: 1;
    }
  }

  img {
    transform: rotate(10deg);
    position: relative;
    z-index: 105;
    ${media.mq[1]} {
      margin-right: 20px;
    }
  }

  ${media.mq[1]} {
    margin-top: 0;
    svg {
      width: 95px;
      height: 95px;
    }
  }
`
